import { fromJS, Map, Set, OrderedMap } from 'immutable';
import * as constants from './constants';
import {
  CREATE_PPSI_DONE,
  SAVE_PPSI_DONE,
  CREATE_PPSI_FAIL,
  SAVE_PPSI_FAIL
} from '../PpsiContainer/constants';
import {
  CREATE_PPI_DONE,
  SAVE_PPI_DONE,
  CREATE_PPI_FAIL,
  SAVE_PPI_FAIL
} from '../PpiContainer/constants';
import {
  SAVE_MARKET_SETTINGS_DONE,
  SAVE_MARKET_SETTINGS_FAIL
} from 'features/MarketSettingContainer/constants';

export const initialState = fromJS({
  market: null,
  reviewMarket: null,
  reviewMarketLanguage: null,
  reviewMarketAvailableLanguage: null,
  language: null,
  configs: Map(),
  isFetchingConfigs: true,
  fetchConfigError: null,
  globalConfigs: Map(),
  isFetchingGlobalConfigs: true,
  fetchGlobalConfigError: null,
  viewsVisited: Set(),
  isDataDirty: false,
  overviewLabels: Map(),
  timeUnits: Map(),
  temperatureUnits: Map(),
  stockHierarchy: Map(),
  firstStorage: Map(),
  shelfLifeTimeUnits: Map(),
  // Types of production, e.g. Oven, Stove. Weirdish name...
  production: Map(),
  displayMarketName: Map(),
  isFetchingName: true,
  fetchingNameError: null,
  //  adding to load market config
  marketConfigs: Map(),
  marketOverviewLabels: Map(),
  marketTimeUnits: Map(),
  marketTemperatureUnits: Map(),
  marketFirstStorage: Map(),
  // marketShelfLifeTimeUnits:Map(),
  marketProduction: Map(),
  isFetchingMarketConfigs: true,
  fetchMarketConfigError: null
});

function mergeConfigForFirstStorage(
  state,
  config,
  configName,
  stateName,
  stateKey = 'code'
) {
  let configsToMerge;
  if (configName === 'firstStorage') {
    configsToMerge = config[configName].reduce(
      (result, configItem) => result.set(configItem.code, fromJS(configItem)),
      OrderedMap()
    );
  }
  return state.get(stateName).mergeWith((prev, next) => {
    const prevInfo = prev.get('info');
    const nextInfo = next.get('info');
    return prev.set('info', prevInfo.merge(nextInfo));
  }, configsToMerge);
}

function mergeConfig(state, config, configName, stateName, stateKey = 'code') {
  let configsToMerge;
  configsToMerge = config[configName].reduce(
    (result, configItem) =>
      result.set(configItem[stateKey], fromJS(configItem)),
    OrderedMap()
  );
  return state.get(stateName).mergeWith((prev, next) => {
    const prevInfo = prev.get('info');
    const nextInfo = next.get('info');
    return prev.set('info', prevInfo.merge(nextInfo));
  }, configsToMerge);
}

function mergeOverviewLabels(state, config) {
  return mergeConfig(state, config, 'overviewLabels', 'overviewLabels', 'name');
}

function mergeTimeUnits(state, config) {
  return mergeConfig(state, config, 'timeUnits', 'timeUnits');
}

function getReviewMarket(config) {
  return config.coreMarket;
}

function getReviewMarketLanguages(config) {
  return config.ifoodPPSILanguageCodes;
}

function mergeTemperatureUnits(state, config) {
  return mergeConfig(state, config, 'temperatureUnits', 'temperatureUnits');
}
function mergeShelfLifeTimeUnits(state, config) {
  return mergeConfig(state, config, 'shelfLifeTimeUnits', 'shelfLifeTimeUnits');
}
function mergeStockHierarchy(state, config) {
  return mergeConfigForFirstStorage(
    state,
    config,
    'firstStorage',
    'firstStorage'
  );
}

function mergeProduction(state, config) {
  return mergeConfig(state, config, 'production', 'production');
}

export default function reducer(state = initialState, { type, payload }) {
  switch (type) {
    case constants.SET_MARKET:
      return state.set('market', fromJS(payload.market));

    case constants.SET_LANGUAGE:
      return state.set('language', fromJS(payload));

    case constants.SET_REVIEW_MARKET_LANGUAGE:
      return state.set('reviewMarketLanguage', fromJS(payload));

    case constants.SET_DATA_DIRTY:
      return state.set('isDataDirty', true);

    case constants.SET_DATA_NOT_DIRTY:
    case CREATE_PPSI_DONE:
    case SAVE_PPSI_DONE:
    case CREATE_PPI_DONE:
    case SAVE_PPI_DONE:
    case SAVE_MARKET_SETTINGS_DONE:
      return state.set('isDataDirty', false).set('callback', null);

    case CREATE_PPSI_FAIL:
    case SAVE_PPSI_FAIL:
    case CREATE_PPI_FAIL:
    case SAVE_PPI_FAIL:
    case SAVE_MARKET_SETTINGS_FAIL:
      return state.set('callback', null);

    case constants.GET_CONFIGS:
      return state.set('isFetchingConfigs', true).set('fetchConfigError', null);

    case constants.GET_CONFIGS_DONE:
      // console.log(payload);
      return state
        .set('configs', fromJS(payload))
        .set('overviewLabels', mergeOverviewLabels(state, payload))
        .set('timeUnits', mergeTimeUnits(state, payload))
        .set('temperatureUnits', mergeTemperatureUnits(state, payload))
        .set('firstStorage', mergeStockHierarchy(state, payload))
        .set('shelfLifeTimeUnits', mergeShelfLifeTimeUnits(state, payload))
        .set('production', mergeProduction(state, payload))
        .set('fetchConfigError', null)
        .set('isFetchingConfigs', false);

    case constants.GET_CONFIGS_FAILED:
      return state
        .set('fetchConfigError', fromJS(payload))
        .set('isFetchingConfigs', false);

    case constants.GET_GLOBAL_CONFIGS:
      return state
        .set('isFetchingGlobalConfigs', true)
        .set('fetchGlobalConfigError', null);

    case constants.GET_GLOBAL_CONFIGS_DONE:
      return state
        .set('overviewLabels', mergeOverviewLabels(state, payload))
        .set('timeUnits', mergeTimeUnits(state, payload))
        .set('temperatureUnits', mergeTemperatureUnits(state, payload))
        .set('firstStorage', mergeStockHierarchy(state, payload))
        .set('shelfLifeTimeUnits', mergeShelfLifeTimeUnits(state, payload))
        .set('production', mergeProduction(state, payload))
        .set('fetchGlobalConfigError', null)
        .set('isFetchingGlobalConfigs', false);

    case constants.GET_GLOBAL_CONFIGS_FAILED:
      return state
        .set('fetchConfigError', fromJS(payload))
        .set('fetchGlobalConfigError', false);

    case constants.SET_USER_IS_GLOBAL:
      return state.set('isFetchingGlobalConfigs', false);

    case constants.VIEW_VISITED:
      return state.set('viewsVisited', state.get('viewsVisited').add(payload));

    case constants.SAVE_AND_CALLBACK:
      return state.set('callback', payload);

    // adding to check market config api call;
    case constants.GET_MARKET_CONFIGS:
      return state
        .set('isFetchingMarketConfigs', true)
        .set('fetchMarketConfigError', null);

    case constants.GET_MARKET_CONFIGS_DONE:
      // console.log(payload);
      return (
        state
          .set('marketConfigs', fromJS(payload))
          .set('reviewMarket', getReviewMarket(payload))
          .set(
            'reviewMarketAvailableLanguage',
            getReviewMarketLanguages(payload)
          )
          .set('reviewMarketLanguage', getReviewMarketLanguages(payload)[0])
          .set('marketOverviewLabels', mergeOverviewLabels(state, payload))
          .set('marketTimeUnits', mergeTimeUnits(state, payload))
          .set('marketTemperatureUnits', mergeTemperatureUnits(state, payload))
          .set('marketFirstStorage', mergeStockHierarchy(state, payload))
          // .set('marketShelfLifeTimeUnits', mergeShelfLifeTimeUnits(state, payload))
          .set('marketProduction', mergeProduction(state, payload))
          .set('fetchMarketConfigError', null)
          .set('isFetchingMarketConfigs', false)
      );

    case constants.GET_MARKET_CONFIGS_FAILED:
      return state
        .set('fetchMarketConfigError', fromJS(payload))
        .set('isFetchingMarketConfigs', false);

    case constants.GET_MARKET_NAME:
      return state.set('isFetchingName', true).set('fetchingNameError', null);

    case constants.GET_MARKET_NAME_DONE:
      return state
        .set('displayMarketName', fromJS(payload))
        .set('fetchingNameError', null)
        .set('isFetchingName', false);

    case constants.GET_MARKET_NAME_FAILED:
      return state
        .set('fetchingNameError', fromJS(payload))
        .set('isFetchingName', false);

    default:
      return state;
  }
}
